import { SimpleSEO } from '@/components/seo/SimpleSEO';
import { MessageCircle, Mail, Users, Code, DollarSign, RefreshCw } from 'lucide-react';

function TwitterIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      className={className}
      style={{
        filter: "drop-shadow(0 0 2px #f7b538)"
      }}
    >
      <path
        fill="#1a1a1a"
        stroke="#f7b538"
        strokeWidth="0.5"
        d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
      />
    </svg>
  );
}

export function Contact() {
  return (
    <>
      <SimpleSEO
        title="Contact Us | MdbApp Support"
        description="Get in touch with MdbApp for support, questions, or feedback. Follow us on Twitter @MobDaBoss"
        keywords="contact MdbApp, support, help, customer service"
      />
      
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8 text-center">Contact Us</h1>

        <div className="space-y-8 text-foreground">
          <section className="text-center">
            <p className="text-lg text-muted-foreground mb-8">
              Have questions, feedback, or need support? We're here to help!
            </p>
          </section>

          <section className="max-w-2xl mx-auto">
            <div className="p-8 rounded-xl border border-[#0A74FF]/20 bg-card text-center space-y-6">
              <div className="flex justify-center">
                <div className="p-4 rounded-full bg-[#0A74FF]/10">
                  <MessageCircle className="w-12 h-12 text-[#0A74FF]" />
                </div>
              </div>
              
              <h2 className="text-2xl font-semibold">Connect With Us on Twitter</h2>
              
              <p className="text-muted-foreground">
                The best way to reach us is through Twitter. Follow us for updates, support, and community engagement.
              </p>

              <div className="pt-4">
                <a 
                  href="https://x.com/intent/follow?screen_name=mobdaboss" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-lg bg-[#0A74FF] text-white hover:bg-[#0A74FF]/80 transition-colors font-semibold text-lg"
                >
                  <TwitterIcon className="w-6 h-6" />
                  Follow @MobDaBoss
                </a>
              </div>

              <div className="pt-6 border-t border-[#0A74FF]/10">
                <p className="text-sm text-muted-foreground">
                  Send us a direct message or mention us in your tweet for support inquiries
                </p>
              </div>
            </div>
          </section>

          <section className="max-w-4xl mx-auto">
            <div className="p-8 rounded-xl border border-[#0A74FF]/20 bg-card space-y-6">
              <h2 className="text-2xl font-semibold text-center">Contact Options</h2>
              <p className="text-center text-muted-foreground">
                Reach out to the right team for your specific needs
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Affiliates */}
                <a
                  href="mailto:affiliates@mdbapp.io"
                  className="p-6 rounded-lg border border-[#0A74FF]/20 hover:bg-[#0A74FF]/5 transition-all hover:-translate-y-1 space-y-3"
                >
                  <div className="flex justify-center">
                    <div className="p-3 rounded-full bg-[#0A74FF]/10">
                      <DollarSign className="w-6 h-6 text-[#0A74FF]" />
                    </div>
                  </div>
                  <h3 className="font-semibold text-center">Affiliates</h3>
                  <p className="text-sm text-muted-foreground text-center">
                    Partnership and affiliate program inquiries
                  </p>
                  <p className="text-xs text-center text-[#0A74FF]">affiliates@mdbapp.io</p>
                </a>

                {/* Developers */}
                <a
                  href="mailto:developers@mdbapp.io"
                  className="p-6 rounded-lg border border-[#0A74FF]/20 hover:bg-[#0A74FF]/5 transition-all hover:-translate-y-1 space-y-3"
                >
                  <div className="flex justify-center">
                    <div className="p-3 rounded-full bg-[#0A74FF]/10">
                      <Code className="w-6 h-6 text-[#0A74FF]" />
                    </div>
                  </div>
                  <h3 className="font-semibold text-center">Developers</h3>
                  <p className="text-sm text-muted-foreground text-center">
                    Technical integration and API support
                  </p>
                  <p className="text-xs text-center text-[#0A74FF]">developers@mdbapp.io</p>
                </a>

                {/* General */}
                <a
                  href="mailto:mdbapp@mdbapp.io"
                  className="p-6 rounded-lg border border-[#0A74FF]/20 hover:bg-[#0A74FF]/5 transition-all hover:-translate-y-1 space-y-3"
                >
                  <div className="flex justify-center">
                    <div className="p-3 rounded-full bg-[#0A74FF]/10">
                      <Mail className="w-6 h-6 text-[#0A74FF]" />
                    </div>
                  </div>
                  <h3 className="font-semibold text-center">General</h3>
                  <p className="text-sm text-muted-foreground text-center">
                    General inquiries and support
                  </p>
                  <p className="text-xs text-center text-[#0A74FF]">mdbapp@mdbapp.io</p>
                </a>

                {/* MDBTDJ */}
                <a
                  href="mailto:mdbtdj@mdbapp.io"
                  className="p-6 rounded-lg border border-[#0A74FF]/20 hover:bg-[#0A74FF]/5 transition-all hover:-translate-y-1 space-y-3"
                >
                  <div className="flex justify-center">
                    <div className="p-3 rounded-full bg-[#0A74FF]/10">
                      <Users className="w-6 h-6 text-[#0A74FF]" />
                    </div>
                  </div>
                  <h3 className="font-semibold text-center">MDBTDJ Inc</h3>
                  <p className="text-sm text-muted-foreground text-center">
                    Dev Team Main Office
                  </p>
                  <p className="text-xs text-center text-[#0A74FF]">mdbtdj@mdbapp.io</p>
                </a>

                {/* MobDaBoss - Main Dev */}
                <a
                  href="mailto:mobdaboss@mdbapp.io"
                  className="p-6 rounded-lg border border-[#0A74FF]/20 hover:bg-[#0A74FF]/5 transition-all hover:-translate-y-1 space-y-3"
                >
                  <div className="flex justify-center">
                    <div className="p-3 rounded-full bg-[#0A74FF]/10">
                      <Code className="w-6 h-6 text-[#0A74FF]" />
                    </div>
                  </div>
                  <h3 className="font-semibold text-center">Mob Da Boss</h3>
                  <p className="text-sm text-muted-foreground text-center">
                    Contact the main app developer
                  </p>
                  <p className="text-xs text-center text-[#0A74FF]">mobdaboss@mdbapp.io</p>
                </a>

                {/* Refunds */}
                <a
                  href="mailto:refunds@mdbapp.io"
                  className="p-6 rounded-lg border border-[#0A74FF]/20 hover:bg-[#0A74FF]/5 transition-all hover:-translate-y-1 space-y-3"
                >
                  <div className="flex justify-center">
                    <div className="p-3 rounded-full bg-[#0A74FF]/10">
                      <RefreshCw className="w-6 h-6 text-[#0A74FF]" />
                    </div>
                  </div>
                  <h3 className="font-semibold text-center">Refunds</h3>
                  <p className="text-sm text-muted-foreground text-center">
                    Refund requests and billing issues
                  </p>
                  <p className="text-xs text-center text-[#0A74FF]">refunds@mdbapp.io</p>
                </a>
              </div>
            </div>
          </section>

          <section className="border-t pt-8">
            <h2 className="text-2xl font-semibold mb-4 text-center">Frequently Asked Questions</h2>
            <p className="text-center text-muted-foreground mb-6">
              Before reaching out, check if your question is answered in our comprehensive guides and documentation.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              <a 
                href="/blog" 
                className="p-4 rounded-lg border border-[#0A74FF]/20 hover:bg-[#0A74FF]/5 transition-colors text-center"
              >
                <h3 className="font-semibold mb-2">📚 Guides & Tutorials</h3>
                <p className="text-sm text-muted-foreground">Step-by-step guides for token creation and management</p>
              </a>
              
              <a 
                href="/blog/create-token-guide" 
                className="p-4 rounded-lg border border-[#0A74FF]/20 hover:bg-[#0A74FF]/5 transition-colors text-center"
              >
                <h3 className="font-semibold mb-2">🚀 Getting Started</h3>
                <p className="text-sm text-muted-foreground">Learn how to create your first token</p>
              </a>
              
              <a 
                href="/privacy-policy" 
                className="p-4 rounded-lg border border-[#0A74FF]/20 hover:bg-[#0A74FF]/5 transition-colors text-center"
              >
                <h3 className="font-semibold mb-2">🔒 Privacy Policy</h3>
                <p className="text-sm text-muted-foreground">How we protect your data and privacy</p>
              </a>
              
              <a 
                href="/refund-policy" 
                className="p-4 rounded-lg border border-[#0A74FF]/20 hover:bg-[#0A74FF]/5 transition-colors text-center"
              >
                <h3 className="font-semibold mb-2">💰 Refund Policy</h3>
                <p className="text-sm text-muted-foreground">Information about our refund policy</p>
              </a>
            </div>
          </section>

          <section className="border-t pt-8">
            <h2 className="text-2xl font-semibold mb-4 text-center">Support Hours</h2>
            <div className="max-w-md mx-auto text-center space-y-2">
              <p className="text-muted-foreground">
                We monitor our Twitter account regularly and aim to respond to inquiries within 24-48 hours.
              </p>
              <p className="text-sm text-muted-foreground">
                For urgent technical issues, please include relevant transaction hashes and wallet addresses in your message.
              </p>
            </div>
          </section>

          <section className="border-t pt-8 bg-yellow-500/10 p-6 rounded-lg">
            <h2 className="text-xl font-semibold mb-4 text-center text-yellow-600">⚠️ Important Security Notice</h2>
            <div className="space-y-2 text-sm">
              <p className="text-center">
                <strong>We will NEVER:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-1 max-w-2xl mx-auto">
                <li>Ask for your private keys or seed phrases</li>
                <li>Request you to send cryptocurrency to any address</li>
                <li>Contact you first via direct message claiming to be support</li>
                <li>Ask you to connect your wallet through a direct message link</li>
              </ul>
              <p className="text-center pt-4">
                Always verify you're communicating with our official Twitter account: <strong>@MobDaBoss</strong>
              </p>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
